import React, { useContext } from 'react';
import _merge from 'lodash/merge';
import List from 'components/List';
import { getContext } from 'common';
import KEY_CODES from 'common/KeyCodes';
import { ElevationProps } from 'components/Elevation';
import { ListItemCascadingContext } from './ListItemCascading';

const {
  ESCAPE,
  ENTER_KEY,
  SPACE_KEY,
  TAB,
  ARROW_LEFT,
} = KEY_CODES;

const CascadingListContext = React.createContext<any>({});

export interface CascadingListProps extends React.ComponentPropsWithRef<'div'> {
  /** The list items for this List may contain check icons.  */
  checkedList?: boolean;
  /** The content of the list, consider using `ListItemBasic`, `ListItemCascading`, `ListItemChecked`, `ListGroupHeading`, and ListDivider */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Override the Elevation properties. See Elevation component for details. */
  elevationProps?: Partial<ElevationProps>;
  /**
   * Callback fired when the component requests to be closed.
   *
   * @param {object} event The event source of the callback.
   */
  onClose?: (arg: React.MouseEvent | React.KeyboardEvent) => void;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  style?: React.CSSProperties;
}

/** `CascadingList` is used as child of `ListItemCascading` for displaying a vertical list of items as a flyout cascading List.
 * All other props than `CascadingList`'s props will be provided to the `List` element. */
const CascadingList = React.forwardRef<HTMLElement, CascadingListProps>((props: CascadingListProps, ref) => getContext(({ theme }) => {
  const {
    children,
    onKeyDown,
    onClick,
    onClose,
    checkedList,
    style,
    theme: propTheme,
    elevationProps,
    ...otherProps
  } = props;
  const listItemCascadingContext = useContext(ListItemCascadingContext);

  const handleKeyDown = (event) => {
    event.stopPropagation();
    onKeyDown && onKeyDown(event);
    switch (event.key) {
      case ESCAPE:
      case TAB:
      case ENTER_KEY:
      case SPACE_KEY:
      case ARROW_LEFT:
        onClose && onClose(event);
        break;
      default:
        break;
    }
  };

  const handleClick = (event) => {
    onClick && onClick(event);
  };
  const userTheme = propTheme.LIST && propTheme.LIST.VERTICAL;
  const resolvedTheme = _merge({}, theme.LIST.VERTICAL, userTheme);
  return (
    <List
      ref={ref}
      theme={resolvedTheme}
      style={{ maxHeight: listItemCascadingContext.listMaxHeight, ...style }}
      elevationProps={elevationProps}
      {...otherProps}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
    >
      <CascadingListContext.Provider value={{ theme: resolvedTheme }}>
        {children}
      </CascadingListContext.Provider>
    </List>
  );
}));

CascadingList.displayName = 'CascadingList';

CascadingList.defaultProps = {
  onClose: undefined,
  checkedList: false,
  theme: {},
  elevationProps: {
    elevationIndex: 8,
    dark: false,
  },
  onKeyDown: undefined,
  onClick: undefined,
  style: {}
};


export {
  CascadingList as default,
  CascadingListContext,
};
